import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import type { SupplierForm } from '@klear/supplier/wire.client'
import React, { useMemo } from 'react'
import type { Draft } from 'immer'
import { Building2, FileStack, Globe, MapPin, type LucideIcon } from 'lucide-react'
import { ORGANIZATION_SIZES } from '@klear/constants'
import { COUNTRY_CODE_OPTIONS, COUNTRY_CODE_US } from '@klear/constants/countries'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'

import type { SupplierFormFieldPath, SupplierFormFieldValue } from '@klear/supplier-ui/supplier-form-state'

export type SupplierFormUpdate = (
  recipe: (draft: Draft<SupplierForm>) => void | SupplierFormFieldPath | readonly SupplierFormFieldPath[] | undefined,
  dirtyPaths?: SupplierFormFieldPath | readonly SupplierFormFieldPath[]
) => void

export interface SupplierFormControlProps {
  values: SupplierForm
  errors: EntityFormErrorMap
  onFieldChange: (path: SupplierFormFieldPath, value: SupplierFormFieldValue) => void
  onFormUpdate: SupplierFormUpdate
}

interface SupplierFieldGroupProps extends SupplierFormControlProps {
  isEditing?: boolean
}

function errorMessage(errors: EntityFormErrorMap, path: SupplierFormFieldPath): string | undefined {
  return errors[path]
}

function FieldShell({
  label,
  htmlFor,
  icon: Icon,
  helperText,
  error,
  className,
  children
}: {
  label: string
  htmlFor?: string
  icon?: LucideIcon
  helperText?: string
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor} className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        {label}
      </Label>
      {children}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
    </div>
  )
}

function TextField({
  path,
  label,
  value,
  errors,
  onFieldChange,
  disabled,
  icon,
  placeholder,
  helperText,
  className
}: {
  path: SupplierFormFieldPath
  label: string
  value: string | number | undefined
  errors: EntityFormErrorMap
  onFieldChange: SupplierFormControlProps['onFieldChange']
  disabled?: boolean
  icon?: LucideIcon
  placeholder?: string
  helperText?: string
  className?: string
}) {
  const message = errorMessage(errors, path)
  return (
    <FieldShell label={label} htmlFor={path} icon={icon} helperText={helperText} error={message} className={className}>
      <Input
        id={path}
        disabled={disabled}
        value={value ?? ''}
        placeholder={placeholder}
        className={message ? 'border-destructive' : ''}
        onChange={(event) => onFieldChange(path, event.target.value)}
      />
    </FieldShell>
  )
}

function SelectField<T extends string | number>({
  path,
  label,
  value,
  options,
  errors,
  onChange,
  disabled,
  placeholder,
  helperText,
  className,
  valueType = 'string'
}: {
  path: SupplierFormFieldPath
  label: string
  value: T | ''
  options: ComboboxOption<T>[]
  errors: EntityFormErrorMap
  onChange: (value: T | '') => void
  disabled?: boolean
  placeholder?: string
  helperText?: string
  className?: string
  valueType?: 'string' | 'number'
}) {
  const message = errorMessage(errors, path)
  return (
    <FieldShell label={label} htmlFor={path} helperText={helperText} error={message} className={className}>
      <Combobox<T>
        value={value}
        disabled={disabled}
        options={options}
        placeholder={placeholder}
        onChange={onChange}
        error={message ? { message } : undefined}
        valueType={valueType}
        className="w-full"
      />
    </FieldShell>
  )
}

export function SupplierOrganizationFields({
  values,
  errors,
  onFieldChange,
  isEditing = true,
  showParentOrganization = false
}: SupplierFieldGroupProps & { showParentOrganization?: boolean }) {
  const supplierSizes = useMemo(
    () =>
      ORGANIZATION_SIZES.map((size) => ({
        value: size,
        label: size
      })),
    []
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organization
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextField
          label="Supplier Name"
          path="name"
          value={values.name}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          label="Supplier Description"
          path="description"
          value={values.description}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        {showParentOrganization ? (
          <TextField
            label="Parent Organization Name"
            path="parentOrganization"
            value={values.parentOrganization}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="Optional"
          />
        ) : null}
        <SelectField
          label="Size"
          path="size"
          value={values.size ?? ''}
          options={supplierSizes}
          errors={errors}
          disabled={!isEditing}
          onChange={(value) => onFieldChange('size', value === '' ? undefined : value)}
        />
      </CardContent>
    </Card>
  )
}

export function SupplierLocationFields({ values, errors, onFieldChange, isEditing = true }: SupplierFieldGroupProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Address & website
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SelectField
          label="Country"
          path="address.country"
          value={values.address.country}
          options={COUNTRY_CODE_OPTIONS}
          errors={errors}
          disabled={!isEditing}
          placeholder="Select a country"
          onChange={(value) => onFieldChange('address.country', value === '' ? COUNTRY_CODE_US : value)}
        />
        <TextField
          label="Address line 1"
          path="address.line1"
          value={values.address.line1}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
          helperText="Optional for some regions"
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="City"
            path="address.city"
            value={values.address.city}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
          <TextField
            label="State / region"
            path="address.state"
            value={values.address.state}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
        </div>
        <TextField
          label="Postal code"
          path="address.postalCode"
          value={values.address.postalCode}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          label="Website"
          path="website"
          value={values.website}
          icon={Globe}
          placeholder="https://example.com"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
      </CardContent>
    </Card>
  )
}

export function SupplierIdentifierFields({ values, errors, onFieldChange, isEditing = true }: SupplierFieldGroupProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileStack className="h-5 w-5" />
          Identifiers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="DUNS Number"
            path="dunsNumber"
            value={values.dunsNumber}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="9 digits"
          />
          <TextField
            label="LEI"
            path="lei"
            value={values.lei}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="ISO 17442 (20 characters)"
          />
          <TextField
            label="EIN / FEIN"
            path="ein"
            value={values.ein}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="9 digits (NN-NNNNNNN)"
          />
          <TextField
            label="VAT Number"
            path="vatNumber"
            value={values.vatNumber}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="Add country prefix if applicable"
          />
        </div>
      </CardContent>
    </Card>
  )
}
